import { DynamicDrawUsage, InstancedMesh, Matrix4, Vector3 } from 'three';
import pigeon from '../../data/pigeon.generated.json';
import type { WorldFeature } from '../types';
import { planActivityGround } from './activityPlan';
import {
  streetAssetGeometry as shape,
  streetInstances,
  mirroredStreetGeometry,
} from './streetAsset';
import { pigeonFootAt, pigeonPoseAt } from './pigeons';

export const pigeonFeature: WorldFeature = {
  id: 'pigeons',
  label: 'Ground pigeons',
  weight: 0.2,
  build(ctx) {
    const crowd = ctx.lifePlan?.crowd;
    if (!crowd) return { objects: [], animated: [], obstacles: [], counts: {} };
    const grounds = planActivityGround(ctx.layout, crowd);
    const birds = grounds.flatMap((g, group) =>
      g.pigeons.map((home, k) => ({
        home,
        group,
        phase: k * 2.399 + group,
        ground: ctx.site.queries.groundHeightAt(home),
      }))
    );
    if (!birds.length) return { objects: [], animated: [], obstacles: [], counts: {} };
    const right = shape(pigeon.wing),
      left = mirroredStreetGeometry(right);
    const material = ctx.materials.painted({ instanced: true });
    const objects = [
      shape(pigeon.body),
      shape(pigeon.head),
      left,
      right,
      shape(pigeon.foot),
      shape(pigeon.foot),
    ].map((geometry, i) => {
      streetInstances(geometry, birds.length, 1810);
      const mesh = new InstancedMesh(geometry, material, birds.length);
      mesh.name = [
        'pigeon-bodies',
        'pigeon-heads',
        'pigeon-left-wings',
        'pigeon-right-wings',
        'pigeon-left-feet',
        'pigeon-right-feet',
      ][i]!;
      mesh.frustumCulled = false;
      mesh.instanceMatrix.setUsage(DynamicDrawUsage);
      return mesh;
    });
    let day = true;
    const base = new Matrix4(),
      local = new Matrix4(),
      scale = new Vector3();
    const animated = {
      id: 'feeding-pigeons',
      setTimeOfDay(_minutes: number, activity: number) {
        day = activity > 0.05;
      },
      update(time: number) {
        birds.forEach((bird, i) => {
          const flightSince = (((time + bird.phase * 9) % 45) + 45) % 45;
          const pose = pigeonPoseAt(
            bird.home,
            day ? time : bird.phase,
            bird.phase,
            day ? flightSince : Infinity
          );
          base
            .makeRotationY(pose.heading)
            .scale(scale.setScalar(day ? 1 : 0))
            .setPosition(pose.x, bird.ground + pose.lift, -pose.y);
          objects[0]!.setMatrixAt(i, base);
          local.makeRotationZ(-pose.peck).setPosition(0.085, 0.255, 0);
          objects[1]!.setMatrixAt(i, local.premultiply(base));
          for (const side of [-1, 1]) {
            local
              .makeRotationX(pose.wing * side)
              .scale(scale.set(1, 1, pose.flying ? 1 : 0.27))
              .setPosition(0, 0.17, 0.035 * side);
            objects[side < 0 ? 2 : 3]!.setMatrixAt(i, local.premultiply(base));
            const foot = pigeonFootAt(pose.walked, side, pose.moving);
            local.makeTranslation(
              foot.forward,
              foot.lift - (pose.flying ? 0 : pose.lift),
              side * 0.045
            );
            objects[side < 0 ? 4 : 5]!.setMatrixAt(i, local.premultiply(base));
          }
        });
        for (const mesh of objects) mesh.instanceMatrix.needsUpdate = true;
      },
    };
    animated.update(0);
    return {
      objects,
      animated: [animated],
      obstacles: [],
      counts: { pigeons: birds.length, pigeonMeshes: objects.length },
    };
  },
};
